import React, { useEffect, useState, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import { Container } from "reactstrap";

// ── Auto-zoom helper: flies to the ship's position whenever it changes ──
const FlyToLocation = ({ lat, lng, zoom = 10 }) => {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) {
      map.flyTo([lat, lng], zoom, { animate: true, duration: 1.5 });
    }
  }, [lat, lng, zoom, map]);
  return null;
};

// ── Arrow (navigation-pointer) SVG icon rotated by heading ──
const createArrowIcon = (hdg = 0) =>
  new L.DivIcon({
    className: "",
    html: `
      <div style="
        width: 40px; height: 40px;
        transform: rotate(${hdg}deg);
        display: flex; align-items: center; justify-content: center;
        filter: drop-shadow(0 2px 6px rgba(0,0,0,0.35));
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
          <!-- Arrow pointing up (north = 0°); heading rotation applied on wrapper div -->
          <polygon
            points="20,4 32,36 20,28 8,36"
            fill="#1d4ed8"
            stroke="#fff"
            stroke-width="2"
            stroke-linejoin="round"
          />
        </svg>
      </div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -22],
  });

const MapPage = () => {
  const navigate = useNavigate();
  const [liveTelemetry, setLiveTelemetry] = useState(null);
  const [telemetryLoading, setTelemetryLoading] = useState(false);

  // ── Live telemetry ──
  const fetchLiveTelemetry = useCallback(async () => {
    setTelemetryLoading(true);
    try {
      const todayStr = "2026-05-09";
      const r = await fetch(
        `https://api.hanoch.in/api/vessel-telemetry/?date=${todayStr}`
      );
      if (!r.ok) throw new Error();
      const j = await r.json();
      setLiveTelemetry(j.data);
    } catch {
      setLiveTelemetry(null);
    } finally {
      setTelemetryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveTelemetry();
    const interval = setInterval(fetchLiveTelemetry, 30_000);
    return () => clearInterval(interval);
  }, [fetchLiveTelemetry]);

  // ── Derived values ──
  const lat = liveTelemetry?.location?.lat;
  const lng = liveTelemetry?.location?.lon;
  const hasPos = lat != null && lng != null;
  const sog = liveTelemetry?.navigation?.sog;
  const heading = liveTelemetry?.navigation?.heading;

  const SHIP_NAME = "ARK Prestige";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <div style={{ display: "flex", height: "100vh", background: "#f5f7fa" }}>

            {/* ── Left Panel ── */}
            <div style={{
              width: "220px",
              padding: "15px",
              overflowY: "auto",
              borderRight: "1px solid #ddd",
              background: "#fff",
            }}>
              <h3>Vessels</h3>

              {telemetryLoading && (
                <div style={{ color: "#64748b", fontSize: 13, marginBottom: 10 }}>
                  ⏳ Fetching live data...
                </div>
              )}

              {/* Live vessel card */}
              <div
                onClick={() => navigate("/vesseldashboard")}
                style={{
                  padding: "12px",
                  marginBottom: "10px",
                  borderRadius: "10px",
                  background: hasPos ? "#f0fdf4" : "#f8f9fa",
                  cursor: "pointer",
                  border: `1px solid ${hasPos ? "#86efac" : "#ddd"}`,
                  position: "relative",
                }}
              >
                {/* Live badge */}
                <div style={{
                  position: "absolute", top: 10, right: 10,
                  display: "flex", alignItems: "center", gap: 5,
                  background: hasPos ? "#dcfce7" : "#f1f5f9",
                  borderRadius: 20, padding: "2px 8px",
                }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: "50%",
                    background: hasPos ? "#16a34a" : "#94a3b8",
                    display: "inline-block",
                    animation: hasPos ? "pulse 2s infinite" : "none",
                  }} />
                  <span style={{ fontSize: 10, fontWeight: 600, color: hasPos ? "#16a34a" : "#94a3b8" }}>
                    {hasPos ? "LIVE" : "NO SIGNAL"}
                  </span>
                </div>

                <h5 style={{ marginBottom: 4 }}>{SHIP_NAME}</h5>

                {hasPos ? (
                  <>
                    <div style={{ fontSize: 12, color: "#374151" }}>
                      📍 {Number(lat).toFixed(4)}°, {Number(lng).toFixed(4)}°
                    </div>
                    {sog != null && (
                      <div style={{ fontSize: 12, color: "#374151" }}>
                        ⚡ SOG: {Number(sog).toFixed(1)} kn
                      </div>
                    )}
                    {heading != null && (
                      <div style={{ fontSize: 12, color: "#374151" }}>
                        🧭 Heading: {Number(heading).toFixed(0)}°
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>
                    No position data available
                  </div>
                )}
              </div>
            </div>

            {/* ── Map ── */}
            <div style={{ flex: 1, position: "relative" }}>
              <MapContainer
                center={hasPos ? [lat, lng] : [9.93, 76.26]}
                zoom={hasPos ? 10 : 7}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  attribution="&copy; OpenStreetMap"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* ── Auto-zoom to live position ── */}
                {hasPos && <FlyToLocation lat={lat} lng={lng} zoom={10} />}

                {/* ── Ship marker with arrow icon ── */}
                {hasPos && (
                  <Marker
                    position={[lat, lng]}
                    icon={createArrowIcon(heading)}
                    eventHandlers={{ click: () => navigate("/vesseldashboard") }}
                  >
                    <Popup>
                      <div>
                        <h4 style={{ margin: "0 0 6px" }}>{SHIP_NAME}</h4>
                        <div>📍 {Number(lat).toFixed(4)}°, {Number(lng).toFixed(4)}°</div>
                        {sog != null && (
                          <div>⚡ SOG: {Number(sog).toFixed(1)} kn</div>
                        )}
                        {heading != null && (
                          <div>🧭 Heading: {Number(heading).toFixed(0)}°</div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>

              {/* Live position badge */}
              <div style={{
                position: "absolute", top: 10, left: 10, zIndex: 1000,
                background: "rgba(255,255,255,0.97)", borderRadius: 8,
                padding: "7px 12px", border: "0.5px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                display: "flex", alignItems: "center", gap: 7,
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: hasPos ? "#16a34a" : "#94a3b8",
                  display: "inline-block",
                  boxShadow: hasPos ? "0 0 0 3px rgba(22,163,74,0.2)" : "none",
                  flexShrink: 0,
                  animation: hasPos ? "pulse 2s infinite" : "none",
                }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: "#1e293b" }}>
                  {hasPos ? "Live Position" : "No Signal"}
                </span>
                {hasPos && (
                  <span style={{ fontSize: 10, color: "#64748b" }}>
                    {Number(lat).toFixed(3)}°, {Number(lng).toFixed(3)}°
                  </span>
                )}
              </div>

              {/* No position overlay */}
              {!hasPos && !telemetryLoading && (
                <div style={{
                  position: "absolute", inset: 0, zIndex: 500,
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  background: "rgba(219,234,254,0.7)",
                  gap: 8, pointerEvents: "none",
                }}>
                  <div style={{ fontSize: 32 }}>📡</div>
                  <div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
                    No live position
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>
                    Check telemetry data
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Pulse animation */}
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50%       { opacity: 0.4; }
            }
          `}</style>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MapPage;